"use client";

import {
  Camera,
  Frame,
  Sun,
  Leaf,
  Sparkles,
  Flower2,
  type LucideIcon,
} from "lucide-react";

/**
 * Per-step "value nugget" (PLA2-340).
 *
 * One short, benefit-led line under the progress bar that tells the consumer
 * *why this step is worth their time* — outcome-led, never "AI". Keyed by the
 * 5 progress nodes (upload → area → preferences → plants → result) plus the
 * transient `generate` state. Shown only in the D2C flow; retailer/Pro flows
 * are unchanged.
 */

type NuggetKey =
  | "upload"
  | "area"
  | "preferences"
  | "plants"
  | "generate"
  | "result";

const NUGGETS: Record<NuggetKey, { icon: LucideIcon; text: string }> = {
  upload: {
    icon: Camera,
    text: "One photo is all it takes. Snap your yard as it is today and we'll show you what it could become.",
  },
  area: {
    icon: Frame,
    text: "Outline just the area you want to change. Everything is sized to fit your real space.",
  },
  preferences: {
    icon: Sun,
    text: "Your zip and sunlight decide what actually thrives — so every plant we suggest is built to last where you live.",
  },
  plants: {
    icon: Leaf,
    text: "Each plant is matched to your climate and light. Adjust quantities or swap any you like before you buy.",
  },
  generate: {
    icon: Sparkles,
    text: "Designing your space across all four seasons — this takes just a moment.",
  },
  result: {
    icon: Sparkles,
    text: "Here's your yard, reimagined through the seasons. Love it? Make it real — plants delivered to your door.",
  },
};

// Container flow (planter → preferences → result), keyed by its own steps.
const CONTAINER_NUGGETS: Record<string, { icon: LucideIcon; text: string }> = {
  planter: {
    icon: Flower2,
    text: "Start with the pot. Every plant count and the soil kit are sized to the planter you pick.",
  },
  location: {
    icon: Sun,
    text: "Your zip decides what thrives on your patio, so every plant we suggest is built to last where you live.",
  },
  sun: {
    icon: Sun,
    text: "Light decides what blooms. We only show recipes that grow in your zone and your planter's sun.",
  },
  style: {
    icon: Leaf,
    text: "Each recipe pairs a thriller, fillers and spillers that grow happily together in one pot.",
  },
  plants: {
    icon: Leaf,
    text: "Keep the plants you love and drop the rest. Counts and spacing re-fit your planter automatically.",
  },
  generating: {
    icon: Sparkles,
    text: "Planting your container. This takes just a moment.",
  },
  result: {
    icon: Sparkles,
    text: "Here's your planter, planted. Love it? Make it real — plants delivered to your door.",
  },
};

export function StepValueNugget({
  step,
  flow = "outdoor",
}: {
  step: string;
  flow?: "outdoor" | "container";
}) {
  const nugget =
    flow === "container" ? CONTAINER_NUGGETS[step] : NUGGETS[step as NuggetKey];
  if (!nugget) return null;

  const Icon = nugget.icon;
  return (
    <div className="mx-auto mt-6 max-w-2xl">
      <div className="flex items-start gap-3 rounded-xl border border-plantista-200 bg-white/70 px-4 py-3 text-left shadow-sm backdrop-blur-sm dark:border-plantista-800 dark:bg-plantista-900/50">
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-plantista-100 text-plantista-600 dark:bg-plantista-800 dark:text-plantista-300">
          <Icon className="h-4 w-4" />
        </span>
        <p className="text-sm font-medium leading-relaxed text-plantista-800 dark:text-plantista-200">
          {nugget.text}
        </p>
      </div>
    </div>
  );
}
